import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import {
  ColumnChooserService,
  ColumnMenuService,
  ColumnModel,
  EditService,
  EditSettingsModel,
  ExcelExportService,
  FilterService,
  GridLine,
  GroupService,
  GroupSettingsModel,
  IEditCell,
  PageService,
  PageSettingsModel,
  PdfExportService,
  ResizeService,
  SelectionSettingsModel,
  SortService,
  SortSettingsModel,
  ToolbarService,
  GridComponent as SyncFusionGridComponent,
  RecordClickEventArgs,
  RecordDoubleClickEventArgs,
  RowDataBoundEventArgs,
  FreezeService,
  AggregateService,
  Aggregate,
  DataStateChangeEventArgs,
  AggregateColumnModel
} from '@syncfusion/ej2-angular-grids'
import { addImage } from './addimage'
import { DataResult } from '@syncfusion/ej2-data';

import { loadCldr, L10n } from '@syncfusion/ej2-base';
import * as numberingSystems from 'cldr-data/supplemental/numberingSystems.json';
import * as gregorian from 'cldr-data/main/pt/ca-gregorian.json';
import * as numbers from 'cldr-data/main/pt/numbers.json';
import * as timeZoneNames from 'cldr-data/main/pt/timeZoneNames.json';
import { DatePicker } from '@syncfusion/ej2-calendars';

loadCldr(numberingSystems, gregorian, numbers, timeZoneNames);

L10n.load({
  'pt': {
    datepicker: { placeholder: 'Selecione uma data' }
  }
});
type GridServerData = Pick<DataResult, 'result' | 'count'>;

@Component({
  selector: 'app-sync-grid',
  providers: [
    PageService,
    SortService,
    FilterService,
    GroupService,
    ColumnChooserService,
    ToolbarService,
    ResizeService,
    ColumnMenuService,
    EditService,
    PdfExportService,
    ExcelExportService,
    FreezeService,
    AggregateService
  ],
  templateUrl: './sync-grid.component.html',
  styleUrls: ['./sync-grid.component.scss'],
})

export class SyncGridComponent implements OnInit, AfterViewInit {
  /**
    * data:
    * - array (client-side) ou
    * - { result: T[]; count: number } (server-side, DataResult compatível)
  */
  @Input() data: any[] | GridServerData;
  @Input() columns: ColumnModel[]
  @Input() emptyRecordsText: string
  @Input() height: string = '69vh'
  @Input() width: string = '100%'
  @Input() allowGroup: boolean = false
  @Input() showColumnMenu: boolean = false
  @Input() allowSelection: boolean = false
  @Input() editSettings?: EditSettingsModel = {
    allowEditing: false,
    allowAdding: false,
    allowDeleting: false,
  }
  @Input() sortSettings?: SortSettingsModel = {}
  @Input() allowPaging: boolean = true
  @Input() allowFiltering: boolean = true
  @Input() showToolbar: boolean = true;
  @Input() serverSide: boolean = false;
  @Input() totalItems: number = 0;
  @Input() pageSettings?: PageSettingsModel = {
    pageSizes: [10, 20, 50],
    pageSize: 10,
    pageCount: 4,
  }
  @Input() selectionSettings: SelectionSettingsModel
  @Input() groupOptions?: GroupSettingsModel
  @Input() aggregateOptions: any[]

  @Input() customSearch: boolean = false
  @Input() isLoading: boolean = false
  @Input() title: string = ''
  @Output() searchAction: EventEmitter<string> = new EventEmitter()
  @Output() onceClick: EventEmitter<any> = new EventEmitter()
  @Output() doubleClick: EventEmitter<any> = new EventEmitter()
  @Output() onDataBound: EventEmitter<any> = new EventEmitter()
  @Output() onRowDataBound: EventEmitter<any> = new EventEmitter()
  @Output() actionSave: EventEmitter<any> = new EventEmitter()
  @Output() ungrouping: EventEmitter<any> = new EventEmitter()
  @Output() grouping: EventEmitter<any> = new EventEmitter()
  @Output() stateChange: EventEmitter<DataStateChangeEventArgs> = new EventEmitter<DataStateChangeEventArgs>();
  @Output() actionBegin: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('grid', { static: true })
  grid: SyncFusionGridComponent
  filterOptions = { type: 'Excel' }
  stringParams?: IEditCell
  lines: GridLine = 'Horizontal'
  imgUrl = addImage
  heightRow: { [key: string]: number } = {
    small: 20,
    medium: 40,
    large: 60,
  }

  constructor() {}

  ngOnInit() {
    //this.groupOptions = { showGroupedColumn: true };
    this.stringParams = {
      params: {
        showClearButton: true,
      },
    }
  }

   ngAfterViewInit(): void {
    let datepicker = new DatePicker({
      locale: 'pt'
    });
    datepicker.appendTo('#element');
  }

  toolbarAction(args: MouseEvent) {
    var selectedRecords: any = this.grid.getSelectedRecords()
    switch ((args.currentTarget as HTMLElement).id) {
      case 'columnButton':
        this.grid.openColumnChooser()
        break
    }
  }

  onActionBegin(args: any) {
    // Emite o evento para o componente pai
    this.actionBegin.emit(args);

    switch(args.requestType){
      case 'save' :
        this.actionSave.emit(args.data)
        args.cancel = true; // Impede o grid de salvar automaticamente
        break;
      case 'ungrouping':
       this.ungrouping.emit(args)
       break;
      case 'grouping':
       this.grouping.emit(args)
       break;
    }
  }

  onDataStateChange(state: DataStateChangeEventArgs) {
    if (this.serverSide) {
      this.stateChange.emit(state);
    }
  }

  onSearch(target: EventTarget | any): void {
    const term = (target && target.value) || '';
    if (this.serverSide || this.customSearch) {
      // server-side: o pai decide; emite a cada tecla
      this.searchAction.emit(term);
    } else {
      // client-side padrão da Syncfusion
      this.grid.search(term);
    }
  }

  recordClick(event: RecordClickEventArgs): void {
    this.onceClick.emit(event.rowData)
  }

  recordDoubleClick(event: RecordDoubleClickEventArgs): void {
    this.doubleClick.emit(event.rowData)
  }

  handleDataBound(): void {
    this.onDataBound.emit();
  }

  getSelectedRecords(): any[] {
    return this.grid.getSelectedRecords()
  }

  selectRow(row): void{
    this.grid.selectRow(row);
  }

  addRow(): void {
    this.grid.addRecord();
  }

  startEdit(): void{
    this.grid.startEdit();
  }

  endEdit(): void{
    this.grid.endEdit();
  }

  closeEdit(): void{
    this.grid.editModule.closeEdit()
  }
  refresh(): void {
    this.grid.refresh()
  }

  groupCollapseAll(): void{
    this.grid.groupCollapseAll();
  }
  showSpinner():  void{
    this.isLoading = true
  }
  hideSpinner():  void{
    this.isLoading = false
  }

}
